import { useState } from 'react'
import classNames from 'classnames'

import { EMPTY_OBJECT } from '../../../constants/common'
import { EMAIL_REPLY_FORM_SHOW_CONVERSATION_BUTTON_CARET_SCROLL_MARGIN } from '../Email.constants'
import {
  useEmailForm,
  useEmailReplyFormConversationButtonOverlap,
} from '../Email.hooks'
import { hasQuotedHistoryBlock } from '../Email.utils'
import {
  EMAIL_CREATION_FORM_FIELD_NAME_BCC,
  EMAIL_CREATION_FORM_FIELD_NAME_CC,
  EMAIL_CREATION_FORM_FIELD_NAME_FROM,
  EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE,
  EMAIL_CREATION_FORM_FIELD_NAME_TO,
  EmailCreationForm,
  isDirtyEmailCreationForm,
} from '../EmailCreationForm'
import { EmailCreationFormInlineContent } from '../EmailCreationFormInlineContent'
import { EmailCreationFormProvider } from '../EmailCreationFormProvider'
import { EmailReplyFormContainer } from '../EmailReplyFormContainer'
import { EmailShowConversationButton } from '../EmailShowConversationButton'

import { getMessageFieldContentStyle } from './EmailReplyForm.utils'

import * as styles from './EmailReplyForm.module.css'

function EmailReplyForm({
  FormContainer = EmailReplyFormContainer,
  containerProps,
  externalRef,
  formBodyActionsProps,
  formFieldsProps: {
    ContentComponent:
      FormFieldsContentComponent = EmailCreationFormInlineContent,
    ...formFieldsProps
  } = EMPTY_OBJECT,
  formProps: {
    className: formClassName = styles.formBody,
    ...formProps
  } = EMPTY_OBJECT,
  getContainerIsDirty,
  getIsDirty = isDirtyEmailCreationForm,
  initialValues,
  recipientsFilterModalInitialValues,
  resizeConfig,
  withServiceAccount = false,
  onCancel,
  onClose,
  onInitEditor,
  onToggleExpandConversation,
  ...restProps
}) {
  const [showExpandConversationButton, setShowExpandConversationButton] =
    useState(() =>
      hasQuotedHistoryBlock(
        initialValues?.[EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]
      )
    )

  const {
    buttonRef: showConversationButtonRef,
    handleInitEditor: handleInitEditorButtonOverlap,
    isCaretUnderButton,
  } = useEmailReplyFormConversationButtonOverlap()

  const {
    blackList,
    checkContainerIsDirty,
    draggingRef,
    fullScreen,
    handleClose,
    handleCloseConfirmation,
    handleCloseModal,
    handleDragOver,
    handleDropFile,
    handleResizeModal,
    isCloseConfirm,
    isDragging,
    isMobileScreenSize,
    modal,
    setBlackList,
    setCloseConfirm,
    setModal,
  } = useEmailForm({
    getContainerIsDirty,
    getIsDirty,
    resizeConfig,
    onClose: onClose || onCancel,
  })

  function handleChangeMessage(value) {
    setShowExpandConversationButton(hasQuotedHistoryBlock(value))
  }

  function handleInitEditor(editor) {
    handleInitEditorButtonOverlap(editor)

    onInitEditor?.(editor)
  }

  const showConversationButtonClassName = classNames(
    styles.showTrimmedContentButton,
    { [styles.showTrimmedContentButtonHidden]: isCaretUnderButton }
  )

  return (
    <EmailCreationFormProvider
      blackList={blackList}
      externalRef={externalRef}
      formBodyActionsProps={formBodyActionsProps}
      getIsDirty={getIsDirty}
      isDragging={isDragging}
      isFullScreen={isMobileScreenSize || fullScreen}
      setBlackList={setBlackList}
      setModal={setModal}
      onCloseModal={handleCloseModal}
      onDragOver={handleDragOver}
      onDrop={handleDropFile}
      onResize={handleResizeModal}
    >
      <EmailCreationForm
        {...restProps}
        FormContainer={FormContainer}
        containerProps={{
          ...containerProps,
          boxProps: {
            ...containerProps?.boxProps,
            ref: draggingRef,
          },
          getIsDirty: checkContainerIsDirty,
          isCloseConfirm,
          isFullScreen: isMobileScreenSize || fullScreen,
          modal,
          setCloseConfirm,
          onCancel,
          onClose: onClose || onCancel,
          onCloseConfirmation: handleCloseConfirmation,
          onResize: handleResizeModal,
        }}
        fieldsProps={{
          ...(recipientsFilterModalInitialValues
            ? {
                [EMAIL_CREATION_FORM_FIELD_NAME_BCC]: {
                  filterModalInitialValues: recipientsFilterModalInitialValues,
                },
                [EMAIL_CREATION_FORM_FIELD_NAME_CC]: {
                  filterModalInitialValues: recipientsFilterModalInitialValues,
                },
                [EMAIL_CREATION_FORM_FIELD_NAME_TO]: {
                  filterModalInitialValues: recipientsFilterModalInitialValues,
                },
              }
            : null),
          ...(withServiceAccount
            ? { [EMAIL_CREATION_FORM_FIELD_NAME_FROM]: { withServiceAccount } }
            : null),
          [EMAIL_CREATION_FORM_FIELD_NAME_MESSAGE]: {
            ...getMessageFieldContentStyle(),
            ...(showExpandConversationButton
              ? {
                  children: (
                    <EmailShowConversationButton
                      className={showConversationButtonClassName}
                      elementRef={showConversationButtonRef}
                      onClick={onToggleExpandConversation}
                    />
                  ),
                }
              : null),
            caretScrollMarginBottom:
              EMAIL_REPLY_FORM_SHOW_CONVERSATION_BUTTON_CARET_SCROLL_MARGIN,
            onChange: handleChangeMessage,
            onInit: handleInitEditor,
          },
        }}
        formFieldsProps={{
          ...formFieldsProps,
          ContentComponent: FormFieldsContentComponent,
          isEmailReplyForm: true,
          templateConfig: { replyMode: true },
        }}
        formProps={{ ...formProps, className: formClassName }}
        initialValues={initialValues}
        onCancel={handleClose}
      />
    </EmailCreationFormProvider>
  )
}

export default EmailReplyForm
